import { FINISHED_GOODS } from 'constants/Scope3Constants/category4.constants';
import { ValidationSchema } from 'enums/CreateBrandModalEnums';
import { CategoryItem, FinishedGoods } from 'models/Scope3/categoryItems.model';
import { toFixedWithoutRounding } from 'shared/utils/toFixedWithoutRounding';
import { validatePercentages } from 'views/ActivityDataEntry/ScopeUpstreamList/CategoryOneComponent/validationSchema';
import * as Yup from 'yup';

const { ERROR_MESSAGE: REQUIRED } = ValidationSchema;
const { TONNE, POUND } = FINISHED_GOODS;
interface TestContextExtended {
  from: {
    value: FinishedGoods;
  }[];
}

const checkTotalValidation = (
  products?: CategoryItem[],
  weightTransported?: number
) => {
  const totalProductQuantity = (products as CategoryItem[]).reduce(
    (sum, product) => {
      let quantityInKg = Number(product?.quantity) ?? 0;
      if (product?.unit === TONNE) {
        quantityInKg *= 1000; // Convert tonnes to kg
      } else if (product?.unit === POUND) {
        quantityInKg *= 0.45359; // Convert pounds to kg
      }
      return sum + quantityInKg;
    },
    0
  );
  return (
    toFixedWithoutRounding(weightTransported ?? 0, 2) ===
    toFixedWithoutRounding(totalProductQuantity, 2)
  );
};

const categoryItemConditionalSchema = (isNumber?: boolean) => {
  const baseSchema = isNumber ? Yup.number() : Yup.string();
  return baseSchema.when('isCompletelyOwnedByCompany', {
    is: isCompletelyOwnedByCompany => !isCompletelyOwnedByCompany,
    then: baseSchema.required(REQUIRED).nullable(),
    otherwise: baseSchema.nullable(),
  });
};

const ownedVehiclePercentageSchema = Yup.number().when(
  'isCompletelyOwnedByCompany',
  {
    is: isCompletelyOwnedByCompany => !isCompletelyOwnedByCompany,
    then: validatePercentages
      .required('Enter a valid number')
      .nullable()
      .min(0, `Owned vehicle percentage must be greater than or equal to 0`)
      .max(99, `Owned vehicle percentage must be lesser than or equal to 99`),
    otherwise: Yup.number().nullable(),
  }
);

const categoryItemSchema = Yup.object({
  supplierId: categoryItemConditionalSchema(true),
  temperatureControl: categoryItemConditionalSchema(),
  ownedVehiclePercentage: ownedVehiclePercentageSchema,
});

const otherSectionCategoryItemSchema = Yup.object({
  transportationCost: categoryItemConditionalSchema(true),
  modeOfTransportation: categoryItemConditionalSchema(),
  unit: categoryItemConditionalSchema(),
  ownedVehiclePercentage: ownedVehiclePercentageSchema,
});

const conditionalSchemaForProducts: Yup.TestFunction = function (this, value) {
  const { from } = this as Yup.TestContext & TestContextExtended;
  const product = this?.parent;

  if (!product) return true;

  const {
    name,
    quantity,
    unit,
    temperatureControl,
    companyPaymentPercentage,
    addressLabel,
  } = product;

  const {
    products,
    totalWeight,
    weightInStorage,
    weightTransported,
    weightSoldViaOnline,
  } = from?.[1]?.value ?? {};

  const fieldsToCheck = [
    name,
    quantity,
    unit,
    temperatureControl,
    companyPaymentPercentage,
    addressLabel,
    totalWeight,
    weightTransported,
    weightInStorage,
    weightSoldViaOnline,
  ];

  const isFilled: boolean = fieldsToCheck.some(field => field != null);

  const isAddressLabel = this?.path.includes('addressLabel');
  const isQuantity = this?.path.includes('quantity');

  if (isAddressLabel && isFilled) {
    const address = this?.parent?.address;
    return (
      address?.latitude && address?.longitude && address?.country && !!value
    );
  }

  if (isQuantity && isFilled) {
    const isEqual = checkTotalValidation(products, weightTransported);

    if (!isEqual) {
      return this.createError({
        message: `Sum of quantities must match weight transported`,
        path: this.path,
      });
    }
  }

  return !isFilled || value != null;
};
const productConditionalTestForString = Yup.string()
  .nullable()
  .test('required-conditional', REQUIRED, conditionalSchemaForProducts);

const productConditionalTestForNumber = validatePercentages
  .nullable()
  .test(
    'required-conditional',
    'Enter a valid number',
    conditionalSchemaForProducts
  );

const productsValidationSchema = Yup.object({
  name: productConditionalTestForString,
  quantity: productConditionalTestForNumber,
  unit: productConditionalTestForString,
  temperatureControl: productConditionalTestForString,
  addressLabel: productConditionalTestForString,
  companyPaymentPercentage: productConditionalTestForNumber,
});

const conditionalSchemaForFinishedGoods = Yup.number()
  .nullable()
  .test('required-conditional', REQUIRED, function (value) {
    const finishedGoods = this?.parent;

    if (!finishedGoods) return true;

    const {
      totalWeight,
      weightTransported,
      weightInStorage,
      weightSoldViaOnline,
      products,
    } = finishedGoods;

    // Check if any field is filled
    const isFilled: boolean =
      [
        totalWeight,
        weightTransported,
        weightInStorage,
        weightSoldViaOnline,
      ].some(field => field != null) ||
      products?.some((product: CategoryItem) => product?.quantity != null);

    const isWeightTransported = this.path.includes('weightTransported');

    if (isWeightTransported && isFilled) {
      const isEqual = checkTotalValidation(products, weightTransported);
      if (!isEqual) {
        return this.createError({
          message: `Weight Transported must match the sum of products' quantities`,
          path: this.path,
        });
      }
    }

    const isBalanced =
      toFixedWithoutRounding(
        weightTransported + weightInStorage + weightSoldViaOnline
      ) == toFixedWithoutRounding(totalWeight);

    if (isFilled && !isBalanced) {
      return this.createError({
        message: `Sum of weights does not add up properly`,
        path: this.path,
      });
    }

    return !isFilled || value != null;
  });

const finishedGoodsValidationSchema = Yup.object({
  totalWeight: conditionalSchemaForFinishedGoods,
  weightTransported: conditionalSchemaForFinishedGoods,
  weightInStorage: conditionalSchemaForFinishedGoods,
  weightSoldViaOnline: conditionalSchemaForFinishedGoods,
  products: Yup.array(productsValidationSchema),
});

export const category4ValidationSchema = Yup.object({
  ingredients: Yup.array(categoryItemSchema),
  packagings: Yup.array(categoryItemSchema),
  others: Yup.array(otherSectionCategoryItemSchema),
  finishedGoods: finishedGoodsValidationSchema,
});
